import React from 'react';
import { View, TouchableOpacity, Platform } from 'react-native';
import { useNavigation, getFocusedRouteNameFromRoute, useRoute } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import * as AppleAuthentication from 'expo-apple-authentication';
import { Ionicons } from '@expo/vector-icons';
import {
    LoginModal,
    LoginWithEmailModal,
    SuccessModal,
    LoginModalTextProps,
    LoginWithEmailModalTextProps,
} from '@double_point/rn-components';
import {
    useGoogleAuth,
    useFacebookAuth,
    GoogleAuthRequestConfig,
    FacebookAuthRequestConfig,
} from '@double_point/rn-utils';
import LoginWithPhoneNumberModal from './LoginWithPhoneNumberModal';
import LoginPartnerModal from './LoginPartnerModal';
import useIcon from 'hooks/useIcon';
import useChangeAuthData from 'hooks/useChangeAuthData';
import AppContext from 'contexts/AppContext';
import OrderContext from 'contexts/OrderContext';
import VenueImageTypes from 'types/enums/VenueImageTypes';
import {
    loginEmail,
    verifyEmailCode,
    getGoogleUserData,
    getFacebookUserData,
    loginWithPhone,
    loginPartner,
    loginApple,
} from 'services/authService';
import storageConstants from 'constants/Storage';
import App from 'constants/App';
import {
    GOOGLE_ENABLED,
    FACEBOOK_ENABLED,
    TERMS_AND_CONDITIONS,
    GOOGLE_EXPO_CLIENT_ID,
    GOOGLE_WEB_CLIENT_ID,
    FACEBOOK_WEB_CLIENT_ID,
    GOOGLE_ANDROID_CLIENT_ID,
    GOOGLE_IOS_CLIENT_ID,
} from '@env';
import { styles } from 'styles/components/appHeader';
import { greenColor } from 'styles/global';
import LoginWithVenuePartner from './LoginWithVenuePartner';

export default (): JSX.Element => {
    const { t } = useTranslation();
    const Icon = useIcon();
    const navigation = useNavigation();
    const route = useRoute();
    const {
        currentAuthData,
        currentVenue,
        setModalizeData,
        currentRouteName,
    } = React.useContext(AppContext);
    const { currentShoppingCart } = React.useContext(OrderContext);
    const venueLogoImage =
        currentVenue?.images.find((image: any) => image.venueImageId === VenueImageTypes.logo)?.path ?? App.defaultImage;
    
    const [isAppleAuthAvailable, setIsAppleAuthAvailable] = React.useState<boolean>(false);
    const { storeCurrentAuthData, deleteCurrentAuthData } = useChangeAuthData();
    const routes = navigation.getState().routes;
    const currentRoute = routes[routes.length - 1];
    const nestedRoute = currentRoute.state?.routes[routes.length - 1];
    const deeplyNestedRoutes = nestedRoute?.state?.routes;
    const commerceScreenInRouteStack = deeplyNestedRoutes?.map((route) => route.name === 'CommerceScreen');
    const homeScreenInRouteStack = deeplyNestedRoutes?.map((route) => route.name === 'HomeScreen');
    const snsScreenInRouteStack = deeplyNestedRoutes?.map((route) => route.name === 'SNSScreen');
    const paymentScreensInRouteStack = deeplyNestedRoutes?.map((route) => {
        switch (route.name) {
            case 'PaymentFDScreen':
                return true;
            case 'AddCardScreen':
                return true;
            case 'PaymentMPPointScreen':
                return true;
            case 'PaymentCloverScreen':
                return true;
            default:
                return false;
        }
    });
    const findCommerceScreenInStack = commerceScreenInRouteStack?.find((stack) => stack === true);
    const findPaymentScreensInStack = paymentScreensInRouteStack?.find((stack) => stack === true);
    const findSNSScreenInStack = snsScreenInRouteStack?.find((stack) => stack === true);
    const homeScreenAsFirstScreen = homeScreenInRouteStack?.length === 1;
    const ordersStackFocused = getFocusedRouteNameFromRoute(route) === 'Orders';
    const eventsStackFocused = getFocusedRouteNameFromRoute(route) === 'Events';
    const articlesStackFocused = getFocusedRouteNameFromRoute(route) === 'Articles';
    const mapStackFocused = getFocusedRouteNameFromRoute(route) === 'Map';
    const params: any = route.params;
    const shouldInsertParamsOnShoppingScreenUrl =
        Platform.OS === 'web' && currentVenue && currentShoppingCart && currentShoppingCart.detail.length > 0;
    const headerIsBeingRenderedOnHomeScreen = currentRouteName === 'HomeScreen';
    // Login Config
    const googleAuthRequestConfig: GoogleAuthRequestConfig = {
        webClientId: GOOGLE_WEB_CLIENT_ID,
        expoClientId: GOOGLE_EXPO_CLIENT_ID,
        androidClientId: GOOGLE_ANDROID_CLIENT_ID,
        iosClientId: GOOGLE_IOS_CLIENT_ID,
    };

    const facebookAuthRequestConfig: FacebookAuthRequestConfig = {
        webClientId: FACEBOOK_WEB_CLIENT_ID,
        expoClientId: FACEBOOK_WEB_CLIENT_ID,
    };

    const { googleSignInAuthResult, googlePromptAsync } = useGoogleAuth(googleAuthRequestConfig);
    const { facebookSignInAuthResult, facebookPromptAsync } = useFacebookAuth(facebookAuthRequestConfig);

    const onGoogleResponse = async (accessToken: string): Promise<void> => {
        const authData = await getGoogleUserData(accessToken);
        if (authData) {
            await storeCurrentAuthData(authData);
            setModalizeData(null);
        }
    };

    const onFacebookResponse = async (accessToken: string): Promise<void> => {
        const authData = await getFacebookUserData(accessToken);
        if (authData) {
            await storeCurrentAuthData(authData);
            setModalizeData(null);
        }
    };

    const onLoginWithAppleDevice = async (user: string, email: string | null) => {
        const getUserWithAppleLogin = await loginApple(user, email);
        if (getUserWithAppleLogin) {
            await storeCurrentAuthData(getUserWithAppleLogin);
            setModalizeData(null);
        }
    };

    React.useEffect(() => {
        if (!googleSignInAuthResult) return;
        onGoogleResponse(googleSignInAuthResult.authentication.accessToken);
    }, [googleSignInAuthResult]);

    React.useEffect(() => {
        if (!facebookSignInAuthResult) return;
        onFacebookResponse(facebookSignInAuthResult.authentication.accessToken);
    }, [facebookSignInAuthResult]);

    React.useEffect(() => {
        const getAppleAuthAvailability = async () => {
            const isAppleAuthAvailable = await AppleAuthentication.isAvailableAsync();
            if (isAppleAuthAvailable) {
                setIsAppleAuthAvailable(isAppleAuthAvailable);
            }
        };
        getAppleAuthAvailability();
    }, []);

    const loginModalTexts: LoginModalTextProps = {
        headerTitle: t('login-modal-header-title'),
        guestButton: t('guest-button'),
        emailButton: t('email-button'),
        phoneButton: t('login-with-cellphone'),
        partnerButton: t('partner-button'),
        facebookButton: t('facebook-button'),
        googleButton: t('google-button'),
        termsAndConditionsTitle: t('terms'),
        tomorrowPartnerButton: t('tomorrow-login-button', { venueName: currentVenue?.name }),
    };

    const loginModalWithEmailTexts: LoginWithEmailModalTextProps = {
        headerEmailTitle: t('login-email-modal-header-title'),
        emailInputPlaceholder: t('login-email-input-placeholder'),
        invalidEmail: t('invalid-email-text'),
        successfullyForwardedEmail: t('successfullyf-orwarded-email'),
        verificationTitle: t('verification-title'),
        wrongCode: t('wrong-code'),
        confirmCode: t('confirm-code'),
        code: t('code'),
        email: t('email'),
        continueAsGuest: t('guest-button'),
        reSendCode: t('re-send-code'),
        changeEmail: t('change-email'),
    };

    const onAbort = (): void => {
        setModalizeData(null);
    };

    const sendEmail = async (email: string): Promise<boolean> => {
        const response = await loginEmail(email);
        if (response) {
            return true;
        } else {
            setModalizeData({
                content: (
                    <SuccessModal
                        isSuccess={false}
                        title={t('error-login-email')}
                        mainColor={currentVenue?.appColor ?? greenColor}
                        message={t('constants.default-error-message')}
                        buttonText={t('return-to-home')}
                        onConfirm={() => {
                            setModalizeData(null);
                            navigation.navigate('Home');
                        }}
                    />
                ),
                closeOnOverlayTap: false,
            });
            return false;
        }
    };

    const sendPartnerDocument = async (partnerDocument: string): Promise<boolean> => {
        if (!currentVenue?.entityIdentificator) return false;
        const authData = await loginPartner(partnerDocument, currentVenue.entityIdentificator);
        if (authData) {
            await storeCurrentAuthData(authData);
            setModalizeData(null);
            return true;
        } else {
            return false;
        }
    };

    const sendValidation = async (email: string, verificationCode: string): Promise<boolean> => {
        const authData = await verifyEmailCode(email, verificationCode);
        if (authData) {
            await storeCurrentAuthData(authData);
            setModalizeData(null);
            return true;
        } else {
            return false;
        }
    };

    const sendPhoneNumber = async (phoneNumber: string): Promise<boolean> => {
        if (!currentVenue) return false;
        const authData = await loginWithPhone(phoneNumber, currentVenue.id);
        if (authData) {
            await storeCurrentAuthData(authData);
            setModalizeData(null);
            return true;
        } else {
            return false;
        }
    };

    const onLoginWithEmail = (): void => {
        setModalizeData({
            content: (
                <LoginWithEmailModal
                    loginEmailStorageKey={storageConstants.loginEmail}
                    sendEmail={sendEmail}
                    sendValidation={sendValidation}
                    onAbort={onAbort}
                    loginEmailColor={currentVenue?.appColor ?? greenColor}
                    localizedTexts={loginModalWithEmailTexts}
                />
            ),
            closeOnOverlayTap: false,
        });
    };

    const onLoginWithVenuePartner = (): void => {
        setModalizeData({
            content: <LoginWithVenuePartner onAbort={onAbort} />,
            closeOnOverlayTap: false,
        });
    };

    const onLoginWithPhoneNumber = (): void => {
        setModalizeData({
            content: (
                <LoginWithPhoneNumberModal
                    onAbort={onAbort}
                    loginPhoneNumberColor={currentVenue?.appColor ?? greenColor}
                    sendPhoneNumber={sendPhoneNumber}
                />
            ),
            closeOnOverlayTap: false,
        });
    };

    const onLoginWithPartnerNumber = (): void => {
        setModalizeData({
            content: (
                <LoginPartnerModal
                    sendPartnerDocument={sendPartnerDocument}
                    onAbort={onAbort}
                    loginPartnerColor={currentVenue?.appColor ?? greenColor}
                />
            ),
            closeOnOverlayTap: false,
        });
    };


    const userWithSession = () => {
        if (!currentAuthData) return;
        return (
            <TouchableOpacity
                style={{ outlineStyle: 'none' }}
                onPress={async () => {
                    await deleteCurrentAuthData();
                }}
            >
                <View style={[styles.contentButton__button, {marginLeft: 0, bottom: 0}]}>
                    <Icon name="user-times" style={styles.contentButton__icon} size={Platform.OS === 'web' ? 22 : 20} />
                </View>
            </TouchableOpacity>
        );
    };

    const userWithoutSession = () => {
        return (
            <TouchableOpacity
                style={{ outlineStyle: 'none'}}
                onPress={() => {
                    setModalizeData({
                        content: (
                            <>
                                <LoginModal
                                    onLoginWithTomorrowCredentials={onLoginWithVenuePartner}
                                    tomorrowLoginEnabled={currentVenue?.useTomorrowPartnerBenefits}
                                    onAbort={onAbort}
                                    googlePromptAsync={googlePromptAsync}
                                    facebookPromptAsync={facebookPromptAsync}
                                    onLoginWithEmail={onLoginWithEmail}
                                    onLoginWithPhoneNumber={onLoginWithPhoneNumber}
                                    onLoginWithPartnerNumber={onLoginWithPartnerNumber}
                                    onLoginWithAppleDevice={onLoginWithAppleDevice}
                                    partnerLoginButtonColor={
                                        currentVenue?.useDifferentIconSet ? undefined : currentVenue?.appColor ?? greenColor
                                    }
                                    venueIcon={venueLogoImage}
                                    guestEnabled={true}
                                    emailEnabled={true}
                                    venueMainColor={currentVenue?.useDifferentIconSet ? currentVenue?.appColor : undefined}
                                    venueSecondaryColor={currentVenue?.appSecondaryColor}
                                    useDifferentIconSet={currentVenue?.useDifferentIconSet}
                                    phoneEnabled={currentVenue?.paymentConfig?.balanceEnabled ?? false}
                                    partnerEnabled={currentVenue?.entityIdentificator ? true : false}
                                    facebookEnabled={FACEBOOK_ENABLED === 'true'}
                                    googleEnabled={GOOGLE_ENABLED === 'true'}
                                    appleLoginEnabled={isAppleAuthAvailable}
                                    termsAndConditionsLink={TERMS_AND_CONDITIONS}
                                    localizedTexts={loginModalTexts}
                                />
                            </>
                        ),
                        closeOnOverlayTap: true,
                    });
                }}
            >
                <View
                    style={[
                        styles.contentButton__button,
                        {marginLeft: 0, bottom: 0},
                        currentVenue?.useDifferentIconSet && { backgroundColor: currentVenue.appSecondaryColor },
                    ]}
                >
                    {currentVenue?.useDifferentIconSet ? (
                        <Ionicons
                            name="person-circle"
                            size={Platform.OS === 'web' ? 32 : 30}
                            style={[styles.profileIconForDifferentSet, { color: currentVenue.appColor }]}
                        />
                    ) : (
                        <Icon name="user-check" style={styles.contentButton__icon} size={Platform.OS === 'web' ? 22 : 20} />
                    )}
                    {/*<Text style={styles.contentButton__text}>{t('login')}</Text>*/}
                </View>
            </TouchableOpacity>
        );
    };

    return (
        <>
            {currentAuthData ? userWithSession() : userWithoutSession()}
        </>
    );
};
